import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { getEventSessionRegistrationInfo } from "@/modules/eventRegistration/features/EventRegistration/api/getEventSessionRegistrationInfo/getEventSessionRegistrationInfoApi";
import { createInitialState, registrationReducer } from "./registrationReducer";
import type {
  Attendee,
  ContactPatch,
  DiscountValueType,
  RegistrationContextType,
} from "./types";

// Re-export so consumers can keep their existing imports working.
export type { Attendee } from "./types";

const RegistrationContext = createContext<RegistrationContextType | null>(null);

interface RegistrationProviderProps {
  eventSessionUuid: string;
  children: React.ReactNode;
}

export function RegistrationProvider({
  eventSessionUuid,
  children,
}: RegistrationProviderProps) {
  const [state, dispatch] = useReducer(
    registrationReducer,
    eventSessionUuid,
    createInitialState,
  );

  // Hydrate event info on mount. Pre-fills contact + default attendee
  // so the user lands on a populated form instead of an empty one.
  useEffect(() => {
    let cancelled = false;

    async function fetchEventInfo() {
      dispatch({ type: "SET_LOADING", loading: true });
      dispatch({ type: "SET_ERROR", error: null });

      try {
        const response =
          await getEventSessionRegistrationInfo(eventSessionUuid);
        if (cancelled) return;

        const info = response.data;
        dispatch({ type: "SET_EVENT_INFO", info });

        if (info.defaultContactName || info.defaultContactEmail) {
          dispatch({
            type: "UPDATE_CONTACT",
            data: {
              contactName: info.defaultContactName || "",
              contactEmail: info.defaultContactEmail || "",
            },
          });
        }

        if (info.defaultAttendee) {
          dispatch({
            type: "SET_ATTENDEES",
            attendees: [
              {
                firstName: info.defaultAttendee.firstName,
                lastName: info.defaultAttendee.lastName,
                email: info.defaultAttendee.email,
                isSelected: true,
                isWaitlist: false,
                specialRequests:
                  info.defaultAttendee.specialRequests || undefined,
                isSelf: true,
              },
            ],
          });
        }
      } catch {
        if (!cancelled) {
          dispatch({
            type: "SET_ERROR",
            error: "Failed to load event information. Please try again.",
          });
        }
      } finally {
        if (!cancelled) {
          dispatch({ type: "SET_LOADING", loading: false });
        }
      }
    }

    fetchEventInfo();
    return () => {
      cancelled = true;
    };
  }, [eventSessionUuid]);

  // Action creators. Public apply/remove helpers stay (callers expect
  // them); internally they dispatch the consolidated SET_* actions.
  const nextStep = useCallback(() => dispatch({ type: "NEXT_STEP" }), []);
  const prevStep = useCallback(() => dispatch({ type: "PREV_STEP" }), []);
  const goToStep = useCallback(
    (step: number) => dispatch({ type: "SET_STEP", step }),
    [],
  );
  const updateContact = useCallback(
    (data: ContactPatch) => dispatch({ type: "UPDATE_CONTACT", data }),
    [],
  );
  const setAttendees = useCallback(
    (attendees: Attendee[]) => dispatch({ type: "SET_ATTENDEES", attendees }),
    [],
  );
  const updateAvailableSeats = useCallback(
    (seats: number) => dispatch({ type: "UPDATE_AVAILABLE_SEATS", seats }),
    [],
  );
  const applyVoucher = useCallback(
    (quantity: number) => dispatch({ type: "SET_VOUCHER", quantity }),
    [],
  );
  const removeVoucher = useCallback(
    () => dispatch({ type: "SET_VOUCHER", quantity: 0 }),
    [],
  );
  const applyDiscountCode = useCallback(
    (code: string, valueType: DiscountValueType, value: number) =>
      dispatch({ type: "SET_DISCOUNT_CODE", code, valueType, value }),
    [],
  );
  const removeDiscountCode = useCallback(
    () =>
      dispatch({
        type: "SET_DISCOUNT_CODE",
        code: null,
        valueType: null,
        value: 0,
      }),
    [],
  );
  const applyAdminDiscount = useCallback(
    (discountType: DiscountValueType, value: number, reason: string) =>
      dispatch({ type: "SET_ADMIN_DISCOUNT", discountType, value, reason }),
    [],
  );
  const removeAdminDiscount = useCallback(
    () =>
      dispatch({
        type: "SET_ADMIN_DISCOUNT",
        discountType: null,
        value: 0,
        reason: "",
      }),
    [],
  );
  const setLoading = useCallback(
    (loading: boolean) => dispatch({ type: "SET_LOADING", loading }),
    [],
  );
  const setError = useCallback(
    (error: string | null) => dispatch({ type: "SET_ERROR", error }),
    [],
  );
  const resetFlow = useCallback(() => dispatch({ type: "RESET_FLOW" }), []);

  const value = useMemo<RegistrationContextType>(
    () => ({
      ...state,
      nextStep,
      prevStep,
      goToStep,
      updateContact,
      setAttendees,
      updateAvailableSeats,
      applyVoucher,
      removeVoucher,
      applyDiscountCode,
      removeDiscountCode,
      applyAdminDiscount,
      removeAdminDiscount,
      setLoading,
      setError,
      resetFlow,
    }),
    [
      state,
      nextStep,
      prevStep,
      goToStep,
      updateContact,
      setAttendees,
      updateAvailableSeats,
      applyVoucher,
      removeVoucher,
      applyDiscountCode,
      removeDiscountCode,
      applyAdminDiscount,
      removeAdminDiscount,
      setLoading,
      setError,
      resetFlow,
    ],
  );

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
}

export function useRegistration(): RegistrationContextType {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error(
      "useRegistration must be used within a RegistrationProvider",
    );
  }
  return context;
}
